import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useContacts } from '../../context/ContactsContext';
import { useMessages } from '../../context/MessagesContext';
import { useUISettings } from '../../context/UISettingsContext';
import { ContactList } from '../contacts/ContactList';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ChatHeader } from './ChatHeader';
import { Button } from '../common/Button';

export function ChatInterface() {
  const { user, twoFAEnabled, pinEnabled, recoveryCodesShown } = useAuth();
  const { selectedContact, showArchived, setShowArchived, contacts } = useContacts();
  const { messages, currentMessage } = useMessages();
  const { 
    setup2FA, 
    disable2FA, 
    setIsPinModalOpen, 
    disablePin, 
    fetchRecoveryCodes,
    fetchCurrentToken,
    rotateKeys,
    showNotification 
  } = useUISettings();

  const handleRotateKeys = async () => {
    const password = prompt('Enter your password to confirm key rotation:');
    if (password) {
      await rotateKeys(password);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA?')) {
      return;
    }
    
    const code = prompt('Enter your 2FA code to disable:');
    if (!code || code.length !== 6) {
      showNotification('Valid 6-digit code required', 'error');
      return;
    }
    
    await disable2FA(code);
  };

  return (
    <div className="flex h-screen bg-gray-200">
      {/* Contacts sidebar */}
      <div className="w-80 bg-white border-r border-gray-300 flex flex-col">
        <div className="p-4 border-b border-gray-300">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg">Contacts</h2>
            <div className="flex space-x-2">
              <button 
                onClick={() => setShowArchived(!showArchived)}
                className={`px-2 py-1 rounded-lg text-sm transition ${
                  showArchived 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
                title={showArchived ? 'Show active contacts' : 'Show archived contacts'}
              >
                📁
              </button>
              <button 
                onClick={() => window.dispatchEvent(new CustomEvent('openContactModal'))}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm transition"
              >
                + Add
              </button>
            </div>
          </div>
          <div className="text-xs text-gray-600 truncate">
            UUID: {user.uuid?.substring(0, 16)}...
          </div>
        </div>
        
        <ContactList />
        
        {/* Security Settings Panel */}
        <div className="p-4 border-t border-gray-300 space-y-2">
<button
  onClick={() => window.dispatchEvent(new CustomEvent('openPrivateKeyModal'))}
  className="w-full bg-teal-500 hover:bg-teal-600 text-white px-3 py-2 rounded-lg text-sm transition flex items-center justify-center"
>
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
  Load Private Key
</button>
          {/* Token display button */}
          <button
            onClick={fetchCurrentToken}
            className="w-full bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm transition flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
            </svg>
            Show Next Token
          </button>

          {/* 2FA Buttons */}
          <div className="flex items-center space-x-2">
            {!twoFAEnabled ? (
              <Button 
                onClick={setup2FA}
                variant="primary"
                size="sm"
                className="flex-1"
              >
                Enable 2FA
              </Button>
            ) : (
              <Button 
                onClick={handleDisable2FA}
                variant="danger"
                size="sm"
                className="flex-1"
              >
                Disable 2FA
              </Button>
            )}
            
            {!pinEnabled ? (
              <Button 
                onClick={() => setIsPinModalOpen(true)}
                variant="primary"
                size="sm"
                className="flex-1"
              >
                Set PIN
              </Button>
            ) : (
              <Button 
                onClick={disablePin}
                variant="danger"
                size="sm"
                className="flex-1"
              >
                Disable PIN
              </Button>
            )}
          </div>
          
          {/* Recovery Codes Button */}
          {!recoveryCodesShown && (
            <button
              onClick={fetchRecoveryCodes}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-lg text-sm transition flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 2H8.828a2 2 0 00-1.414.586l-1.121 1.121A2 2 0 005.586 4H4zm6 3a4 4 0 100 8 4 4 0 000-8zm0 2a2 2 0 110 4 2 2 0 010-4z" clipRule="evenodd" />
              </svg>
              Get Recovery Codes
            </button>
          )}

          {/* Key Rotation and Logout */}
          <div className="flex items-center space-x-2">
            <Button 
              onClick={handleRotateKeys}
              variant="warning"
              size="sm"
              className="flex-1"
            >
              Rotate Keys
            </Button>
            <Button 
              onClick={() => window.dispatchEvent(new CustomEvent('logout'))}
              variant="danger"
              size="sm"
              className="flex-1"
            >
              Log Out
            </Button>
          </div>
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        {selectedContact ? (
          <>
            <ChatHeader contact={selectedContact} />
            <MessageList messages={messages} selectedContact={selectedContact} />
            <MessageInput />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="bg-gray-100 p-8 rounded-full mb-4 inline-block">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-lg font-medium">Select a contact</p>
              <p className="text-sm">Choose a contact from the sidebar to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}